/* eslint-disable react/jsx-no-bind */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';
import moment from 'moment-timezone';
import { selectSegment } from '@actions/map-actions';
import { colors } from '@constants/colors';
import { Icon } from '@mui';
import { getTrayTop } from '@selectors/map-selector';
import { shapeDistance } from '@utils/geometry-utils';
import {
  getSegmentTitle,
  isScheduleEnabled,
  renderScheduleString,
  renderSmallWarning,
  showEntityScheduleDateFilterWarning
} from '@utils/segment-schedule/common';
import { enDash } from '@utils/shared-utils';
import './details-table-locations-list.scss';

const DetailsTableLocationsList = ({ entity }) => {
  const dispatch = useDispatch();

  const mapFilters = useSelector(state => state.map.filters);
  const segmentImpactTypes = useSelector(state => state.dataTypes.segment_impact_type);
  const segmentDirections = useSelector(state => state.dataTypes.segment_direction);
  const selectedPosition = useSelector(state => state.map.selectedPosition);
  const selectedRadius = useSelector(state => state.map.selectedRadius);
  const selectedBounds = useSelector(state => state.map.selectedBounds);
  const tray = useSelector(getTrayTop);

  const [expandAll, setExpandAll] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const setLocationActive = useCallback(segmentId => {
    const { selectedSegment } = tray;
    if (selectedSegment && selectedSegment.id === segmentId) {
      dispatch(selectSegment(null));
    } else {
      dispatch(selectSegment(segmentId));
    }
  }, [dispatch, tray]);

  const toggleExpandAll = useCallback(() => setExpandAll(prev => !prev), []);

  const toggleShowAll = useCallback(() => setShowAll(prev => !prev), []);

  const clickedSegment = useMemo(() => {
    const { segments } = entity;
    const { lat, lng } = selectedPosition;
    const newSegments = [...segments];

    const distanceSegments = (
      newSegments
        .filter(segment => !segment.bounds || selectedBounds.intersects(segment.bounds))
        .map(segment => ({ distance: shapeDistance([lng, lat], segment.shape), segment }))
        .filter(distanceSegment => distanceSegment.distance <= selectedRadius)
    );
    return (
      distanceSegments
        .sort((first, second) => first.distance - second.distance)
        .map(distanceSegment => distanceSegment.segment)
    );
  }, [entity, selectedBounds, selectedPosition, selectedRadius]);

  const schedules = useMemo(() => {
    const scheduleEnabled = isScheduleEnabled(entity.type_name);
    if (!scheduleEnabled) {
      return null;
    }
    const { segments } = entity;
    const active = [];
    const upcoming = [];
    const completed = [];
    const noSchedule = [];
    segments.forEach(segment => {
      if (segment.schedules && segment.schedules.length) {
        const start = moment(segment.schedules[0].start_date).toDate();
        const end = moment(segment.schedules[0].end_date).toDate();
        const today = moment().toDate();
        if (end < today) {
          completed.push(segment);
        } else if (start > today) {
          upcoming.push(segment);
        } else {
          active.push(segment);
        }
      } else {
        noSchedule.push(segment);
      }
    });
    return { active, upcoming, completed, noSchedule };
  }, [entity]);

  const getLocationCards = useCallback(segments => {
    const { selectedSegment } = tray;
    return segments.map(segment => {
      const impactType = R.find(R.propEq(segment.impact_type, 'id'))(Object.values(segmentImpactTypes));
      const direction = R.find(R.propEq(segment.direction, 'id'))(Object.values(segmentDirections));

      return (
        <div
          styleName={`location-card ${selectedSegment && selectedSegment.id === segment.id ? 'active-card' : ''}`}
          key={segment.id}
          onClick={() => setLocationActive(segment.id)}
          role="presentation"
        >
          <div styleName="title">
            {getSegmentTitle(segment)}
          </div>
          <div styleName="row">{segment.description}</div>
          { direction && impactType &&
            <div styleName="row">{direction.name} &bull; {impactType.name}</div>
          }
          {segment.schedules && segment.schedules.length > 0 &&
            <div styleName="schedule">{renderScheduleString(segment.schedules)}</div>
          }
          {(expandAll || (selectedSegment && selectedSegment.id === segment.id)) &&
            <div styleName="street-info">
              <div styleName="row">
                <div styleName="street-label">From</div>
                <div styleName="street-value">{segment.from_street || segment.from_address || enDash}</div>
              </div>
              <div styleName="row">
                <div styleName="street-label">To</div>
                <div styleName="street-value">{segment.to_street || segment.to_address || enDash}</div>
              </div>
            </div>
          }
        </div>
      );
    });
  }, [expandAll, segmentDirections, segmentImpactTypes, setLocationActive, tray]);

  const { segments } = entity;
  const scheduleEnabled = isScheduleEnabled(entity.type_name);
  const showHiddenLocationsWarning = showEntityScheduleDateFilterWarning(mapFilters, entity);

  return (
    <div styleName="locations-container">
      <div styleName="header">
        Locations<div styleName="location-count">{segments.length}</div>
        {showAll &&
          <div styleName="expand-icon">
            {expandAll ?
              <Icon role="img" onClick={toggleExpandAll}>unfold_less</Icon> :
              <Icon role="img" onClick={toggleExpandAll}>unfold_more</Icon>
            }
          </div>
        }
      </div>
      {showHiddenLocationsWarning && renderSmallWarning('Some locations may be hidden on the map because the date filter is applied.')}
      {!showAll && clickedSegment.length > 0 &&
        <div>
          <div styleName="locations-list">{getLocationCards(clickedSegment)}</div>
        </div>
      }
      {scheduleEnabled ?
        <div>
          {showAll && schedules.noSchedule.length > 0 &&
            <div>
              <div styleName="label">FOLLOWING EVENT SCHEDULE</div>
              <div styleName="locations-list">{getLocationCards(schedules.noSchedule)}</div>
            </div>
          }
          {showAll && schedules.active.length > 0 &&
            <div>
              <div styleName="label">CURRENTLY ACTIVE</div>
              <div styleName="locations-list">{getLocationCards(schedules.active)}</div>
            </div>
          }
          {showAll && schedules.upcoming.length > 0 &&
            <div>
              <div styleName="label">UPCOMING</div>
              <div styleName="locations-list">{getLocationCards(schedules.upcoming)}</div>
            </div>
          }
          {showAll && schedules.completed.length > 0 &&
            <div>
              <div styleName="label">COMPLETED</div>
              <div styleName="locations-list">{getLocationCards(schedules.completed)}</div>
            </div>
          }
        </div> :
        <div>
          {showAll && segments.length > 0 &&
            <div>
              <div styleName="locations-list">{getLocationCards(segments)}</div>
            </div>
          }
        </div>
      }
      {segments.length > 0 &&
        <div styleName="show-more" onClick={toggleShowAll} role="presentation">
          <Icon role="img" color={colors.opacity.black0_6} style={{ paddingRight: '12px', width: '24px'}}>more_horiz</Icon>
          <div>{!showAll ? 'SHOW MORE' : 'SHOW LESS'}</div>
        </div>
      }
    </div>
  );
};

DetailsTableLocationsList.propTypes = {
  entity: PropTypes.object
};

export default DetailsTableLocationsList;
